import { LauncherElementData, TreeNode } from '../../hooks/stores/launcherElementsStore';
import { LauncherElementType, launcherElementInfo } from '../../info/launcherElementInfo';

import { UIData } from '../../hooks/stores/uiStore';
import { cleanTree } from '../../functions/launcherConfig/cleanTree';
import { deepMergeObjects } from '../../functions/utils/deepMergeObjects';
import { flattenTree } from '../../functions/launcherConfig/flattenTree';
import { launcherStatesOrder } from '../../hooks/stores/launcherStatesStore';
import { moveNodesToParentByID } from '../../functions/launcherElement/moveNodesToParentByID';
import { removeNodeByID } from '../../functions/launcherElement/removeNodeByID';

interface ElementManipulationDependencies {
  UIData: UIData;
  setUIData: (data: Partial<UIData>) => void;
  launcherWindowData: { children: TreeNode[] };
  setLauncherWindowData: (data: Partial<{ children: TreeNode[] }>) => void;
  launcherElementsData: Record<string, LauncherElementData>;
  setLauncherElementsData: (data: Partial<Record<string, LauncherElementData>>, replace?: boolean) => void;
  updateElementData: (id: string, data: { [key: string]: any }, propertyPath?: string) => void;
  deselectAllElements: () => void;
  selectOneElement: (id: string) => void;
}

export function createElementManipulationUtils({
  UIData,
  setUIData,
  launcherWindowData,
  setLauncherWindowData,
  launcherElementsData,
  setLauncherElementsData,
  updateElementData,
  deselectAllElements,
  selectOneElement,
}: ElementManipulationDependencies) {
  const deleteElement = (id: string) => {
    let resultElementsTree: TreeNode[] | undefined = [
      ...launcherWindowData.children,
    ];
    const resultLauncherElementsData = { ...launcherElementsData };

    const [newElementsTree, removedNode, removedIDs] = removeNodeByID({
      treeNode: resultElementsTree,
      id,
    });
    resultElementsTree = newElementsTree;

    removedIDs.forEach((id) => {
      delete resultLauncherElementsData[id];
    });

    setLauncherWindowData({ children: resultElementsTree });
    setLauncherElementsData(resultLauncherElementsData, true);
    deselectAllElements();
  };

  const deleteSelectedElements = () => {
    let resultElementsTree: TreeNode[] | undefined = [
      ...launcherWindowData.children,
    ];
    const resultLauncherElementsData = { ...launcherElementsData };

    UIData.selectedElementIDs.forEach((id) => {
      const [newElementsTree, removedNode, removedIDs] = removeNodeByID({
        treeNode: resultElementsTree,
        id,
      });
      resultElementsTree = newElementsTree;

      removedIDs.forEach((id) => {
        delete resultLauncherElementsData[id];
      });
    });

    setLauncherWindowData({ children: resultElementsTree });
    setLauncherElementsData(resultLauncherElementsData, true);
    deselectAllElements();
  };

  const renameElement = (id: string, name: string) =>
    updateElementData(id, { name }, '');

  const moveElementsToAnotherParent = ({
    elementIDs,
    parentID,
    index,
  }: {
    elementIDs: string[];
    parentID?: string;
    index?: number;
  }) => {
    const resultTree = moveNodesToParentByID({
      tree: launcherWindowData.children,
      nodeIDs: elementIDs,
      parentID,
      index,
    });

    elementIDs.forEach((id) => {
      updateElementData(
        id,
        {
          position: 'relative',
          top: undefined,
          bottom: undefined,
          left: undefined,
          right: undefined,
        },
        'props.class'
      );
    });

    setLauncherWindowData({ children: resultTree });
  };

  const _changeIDsRecursively = (node: LauncherElementData) => {
    node.id = crypto.randomUUID();
    if (node.props.children) {
      node.props.children.forEach((child: LauncherElementData) =>
        _changeIDsRecursively(child)
      );
    }
  };

  const addElement = (
    elementType: LauncherElementType,
    data?: Partial<LauncherElementData>
  ): LauncherElementData => {
    const id = launcherElementInfo[elementType].id;
    const name = launcherElementInfo[elementType].name;
    const defaultProps = launcherElementInfo[elementType]
      .defaultProps as LauncherElementData['props'];
    let defaultChildren =
      launcherElementInfo[elementType].defaultProps?.children;

    if (defaultChildren?.length > 0) {
      defaultChildren = processChildren(defaultChildren);
    }

    let newElement: LauncherElementData = {
      id: id || crypto.randomUUID(),
      // TODO: add back licensePopup functionality
      // type: elementType === 'licensePopup' ? 'popup' : elementType,
      type: elementType,
      name:
        elementType === 'button' ||
        elementType === 'variableText' ||
        elementType === 'text' ||
        elementType === 'link'
          ? undefined
          : name,
      props: {
        ...defaultProps,
        children:
          defaultChildren !== undefined ? [...defaultChildren] : undefined,
      },
    };

    newElement = deepMergeObjects(
      newElement,
      JSON.parse(JSON.stringify(data || {}))
    ) as LauncherElementData;

    setLauncherElementsData({
      [newElement.id]: newElement,
      ...(flattenTree([newElement], (node) => node?.props?.children) as {
        [key: string]: LauncherElementData;
      }),
    });
    setLauncherWindowData({
      children: [
        ...launcherWindowData.children,
        ...cleanTree([newElement], (node) => node?.props?.children),
      ],
    });

    newElement.props.class.variants = Object.fromEntries(
      launcherStatesOrder.map((key) => [key, { true: {}, false: {} }])
    );

    selectOneElement(newElement.id);
    setUIData({
      currentLauncherState: [null, null],
      elementInEditingModeID: undefined,
    });

    return newElement;
  };

  const processChildren = (children: any[]): any[] => {
    return children.map((child) => ({
      id: crypto.randomUUID(),
      type: child.type,
      name: child.name,
      props: {
        ...child.props,
        children: child.props.children ? processChildren(child.props.children) : undefined,
      },
    }));
  };

  return {
    deleteElement,
    deleteSelectedElements,
    renameElement,
    moveElementsToAnotherParent,
    addElement,
  };
} 